import QRCode from 'qrcode';
import { isAr, message, saveBlob, formatBytes, showFileAdvisory } from './extra-common.js';
import { trackEvent } from '../core/analytics.js';
import {
  ALL_COUNTRIES,
  detectUserCountry,
  populateCountrySelect,
  sanitizePhoneNumber,
  setupPhoneInputSync,
  attachModernPhoneInput
} from './countries-data.js';

const byId = (id) => document.getElementById(id);
const app = byId('qr-app');
const canvas = document.createElement('canvas');
canvas.className = 'qr-canvas-preview'; canvas.setAttribute('role', 'img'); canvas.setAttribute('aria-label', isAr ? 'معاينة رمز QR' : 'QR code preview');
byId('qr-output-container').replaceChildren(canvas);
let svgOutput = '';
let logoImage = null;
let logoDataUrl = '';
let renderToken = 0;
const formFields = ['qr-content-type', 'qr-input-text', 'qr-ssid', 'qr-wifi-password', 'qr-wifi-security', 'qr-wifi-hidden', 'qr-wa-dial-code', 'qr-whatsapp-number', 'qr-whatsapp-message', 'qr-email-address', 'qr-email-subject', 'qr-email-message', 'qr-phone-number', 'qr-phone-number-sms', 'qr-sms-message', 'qr-vcard-name', 'qr-vcard-phone', 'qr-vcard-email', 'qr-vcard-organization', 'qr-latitude', 'qr-longitude', 'qr-fg-color', 'qr-bg-color', 'qr-correction', 'qr-size', 'qr-style'];

function escapeWifi(value) { return String(value).replace(/[\\;,:\"]/g, (match) => `\\${match}`); }
function payload() {
  const value = (id) => byId(id)?.value.trim() || '';
  switch (value('qr-content-type')) {
    case 'wifi': {
      const ssid = value('qr-ssid'); const security = value('qr-wifi-security'); const password = value('qr-wifi-password');
      if (!ssid) throw new Error(isAr ? 'أدخل اسم شبكة Wi-Fi.' : 'Enter a Wi-Fi network name.');
      if (security !== 'nopass' && !password) throw new Error(isAr ? 'أدخل كلمة مرور الشبكة.' : 'Enter the network password.');
      return `WIFI:T:${security};S:${escapeWifi(ssid)};${security === 'nopass' ? '' : `P:${escapeWifi(password)};`}${byId('qr-wifi-hidden').checked ? 'H:true;' : ''};`;
    }
    case 'whatsapp': {
      const dial = value('qr-wa-dial-code');
      const rawPhone = value('qr-whatsapp-number');
      const phone = sanitizePhoneNumber(dial, rawPhone);
      if (!phone) throw new Error(isAr ? 'أدخل رقم الهاتف مع رمز الدولة.' : 'Enter a phone number with its country code.');
      const messageText = value('qr-whatsapp-message');
      return `https://wa.me/${phone}${messageText ? `?text=${encodeURIComponent(messageText)}` : ''}`;
    }
    case 'email': {
      const address = value('qr-email-address');
      if (!address) throw new Error(isAr ? 'أدخل البريد الإلكتروني.' : 'Enter an email address.');
      const params = new URLSearchParams(); if (value('qr-email-subject')) params.set('subject', value('qr-email-subject')); if (value('qr-email-message')) params.set('body', value('qr-email-message'));
      const query = params.toString();
      return `mailto:${address}${query ? `?${query}` : ''}`;
    }
    case 'phone': { const phone = value('qr-phone-number'); if (!phone) throw new Error(isAr ? 'أدخل رقم الهاتف.' : 'Enter a phone number.'); return `tel:${phone}`; }
    case 'sms': { const phone = value('qr-phone-number-sms'); if (!phone) throw new Error(isAr ? 'أدخل رقم الهاتف.' : 'Enter a phone number.'); return `SMSTO:${phone}:${value('qr-sms-message')}`; }
    case 'vcard': {
      const name = value('qr-vcard-name'); if (!name) throw new Error(isAr ? 'أدخل الاسم.' : 'Enter a name.');
      const vcard = (text) => text.replace(/\\/g, '\\\\').replace(/\r?\n/g, '\\n').replace(/,/g, '\\,').replace(/;/g, '\\;');
      return ['BEGIN:VCARD', 'VERSION:3.0', `FN:${vcard(name)}`, value('qr-vcard-organization') && `ORG:${vcard(value('qr-vcard-organization'))}`, value('qr-vcard-phone') && `TEL:${value('qr-vcard-phone')}`, value('qr-vcard-email') && `EMAIL:${value('qr-vcard-email')}`, 'END:VCARD'].filter(Boolean).join('\n');
    }
    case 'location': {
      if (!value('qr-latitude') || !value('qr-longitude')) throw new Error(isAr ? 'أدخل إحداثيات صحيحة للموقع.' : 'Enter valid coordinates for this location.');
      const lat = Number(value('qr-latitude')); const lon = Number(value('qr-longitude'));
      if (!Number.isFinite(lat) || !Number.isFinite(lon) || Math.abs(lat) > 90 || Math.abs(lon) > 180) throw new Error(isAr ? 'أدخل إحداثيات صحيحة للموقع.' : 'Enter valid coordinates for this location.');
      return `https://maps.google.com/?q=${lat},${lon}`;
    }
    default: {
      const text = value('qr-input-text'); if (!text) throw new Error(isAr ? 'أدخل رابطًا أو نصًا لإنشاء الرمز.' : 'Enter text or a URL to create a code.'); return text;
    }
  }
}

function isFinder(row, col, n) {
  return (row < 7 && col < 7) || (row < 7 && col >= n - 7) || (row >= n - 7 && col < 7);
}
function roundedRect(ctx, x, y, width, height, radius) {
  const r = Math.min(radius, width / 2, height / 2);
  ctx.beginPath(); ctx.roundRect(x, y, width, height, r); ctx.fill();
}
function drawFinder(ctx, x, y, cell, color, background, style) {
  const area = cell * 7; const radius = style === 'square' ? 0 : cell * 1.15;
  ctx.fillStyle = color; roundedRect(ctx, x, y, area, area, radius);
  ctx.fillStyle = background; roundedRect(ctx, x + cell, y + cell, cell * 5, cell * 5, style === 'square' ? 0 : cell * 0.8);
  ctx.fillStyle = color;
  if (style === 'dots') { ctx.beginPath(); ctx.arc(x + cell * 3.5, y + cell * 3.5, cell * 1.45, 0, Math.PI * 2); ctx.fill(); }
  else roundedRect(ctx, x + cell * 2, y + cell * 2, cell * 3, cell * 3, style === 'square' ? 0 : cell * 0.65);
}
function drawMatrix(matrix, size, style, foreground, background) {
  const ctx = canvas.getContext('2d');
  canvas.width = size; canvas.height = size;
  ctx.fillStyle = background; ctx.fillRect(0, 0, size, size);
  const n = matrix.size; const margin = 4; const cell = size / (n + margin * 2); const offset = cell * margin;
  ctx.fillStyle = foreground;
  for (let row = 0; row < n; row++) for (let col = 0; col < n; col++) {
    if (!matrix.get(row, col) || isFinder(row, col, n)) continue;
    const x = offset + col * cell; const y = offset + row * cell;
    if (style === 'dots') { ctx.beginPath(); ctx.arc(x + cell / 2, y + cell / 2, Math.max(0.5, cell * 0.43), 0, Math.PI * 2); ctx.fill(); }
    else if (style === 'rounded') roundedRect(ctx, x + cell * 0.035, y + cell * 0.035, cell * 0.93, cell * 0.93, cell * 0.35);
    else ctx.fillRect(Math.round(x), Math.round(y), Math.ceil(cell), Math.ceil(cell));
  }
  drawFinder(ctx, offset, offset, cell, foreground, background, style);
  drawFinder(ctx, offset + (n - 7) * cell, offset, cell, foreground, background, style);
  drawFinder(ctx, offset, offset + (n - 7) * cell, cell, foreground, background, style);
  if (logoImage) {
    const tileSize = Math.min(size * 0.26, (n - 6) * cell);
    const logoSize = tileSize * 0.88;
    const x = (size - tileSize) / 2;
    const y = x;
    ctx.fillStyle = background;
    roundedRect(ctx, x, y, tileSize, tileSize, tileSize * 0.18);
    ctx.drawImage(logoImage, (size - logoSize) / 2, (size - logoSize) / 2, logoSize, logoSize);
  }
}
function makeSvg(matrix, size, style, foreground, background) {
  const n = matrix.size; const margin = 4; const cell = size / (n + margin * 2); const offset = margin * cell; const parts = [`<svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 ${size} ${size}"><rect width="100%" height="100%" fill="${background}"/><g fill="${foreground}">`];
  for (let row = 0; row < n; row++) for (let col = 0; col < n; col++) {
    if (!matrix.get(row, col) || isFinder(row, col, n)) continue;
    const x = offset + col * cell; const y = offset + row * cell;
    if (style === 'dots') parts.push(`<circle cx="${x + cell / 2}" cy="${y + cell / 2}" r="${cell * 0.43}"/>`);
    else { const radius = style === 'rounded' ? cell * 0.34 : 0; parts.push(`<rect x="${x}" y="${y}" width="${cell}" height="${cell}" rx="${radius}"/>`); }
  }
  const finder = (x, y) => {
    const r = style === 'square' ? 0 : cell;
    parts.push(`<rect x="${x}" y="${y}" width="${cell * 7}" height="${cell * 7}" rx="${r}"/><rect x="${x + cell}" y="${y + cell}" width="${cell * 5}" height="${cell * 5}" rx="${r}" fill="${background}"/>`);
    if (style === 'dots') parts.push(`<circle cx="${x + cell * 3.5}" cy="${y + cell * 3.5}" r="${cell * 1.45}"/>`);
    else parts.push(`<rect x="${x + cell * 2}" y="${y + cell * 2}" width="${cell * 3}" height="${cell * 3}" rx="${r}"/>`);
  };
  finder(offset, offset); finder(offset + (n - 7) * cell, offset); finder(offset, offset + (n - 7) * cell); parts.push('</g>');
  if (logoDataUrl) {
    const tileSize = Math.min(size * 0.26, (n - 6) * cell);
    const logoSize = tileSize * 0.88;
    const p = (size - tileSize) / 2;
    const lp = (size - logoSize) / 2;
    parts.push(`<rect x="${p}" y="${p}" width="${tileSize}" height="${tileSize}" rx="${tileSize * 0.18}" fill="${background}"/><image href="${logoDataUrl}" x="${lp}" y="${lp}" width="${logoSize}" height="${logoSize}" preserveAspectRatio="xMidYMid meet"/>`);
  }
  parts.push('</svg>'); return parts.join('');
}
function relativeLuminance(hex) {
  const rgb = [1, 3, 5].map((start) => parseInt(hex.slice(start, start + 2), 16) / 255).map((value) => value <= 0.04045 ? value / 12.92 : ((value + 0.055) / 1.055) ** 2.4);
  return (0.2126 * rgb[0]) + (0.7152 * rgb[1]) + (0.0722 * rgb[2]);
}
function contrastRatio(foreground, background) {
  const first = relativeLuminance(foreground); const second = relativeLuminance(background);
  return (Math.max(first, second) + 0.05) / (Math.min(first, second) + 0.05);
}

function setType() {
  const selected = byId('qr-content-type').value;
  app.querySelectorAll('[data-qr-type]').forEach((section) => { section.hidden = section.dataset.qrType !== selected; });
  render();
}

function getStyle() {
  return document.querySelector('input[name="qr-style-choice"]:checked')?.value || byId('qr-style')?.value || 'square';
}

function render() {
  const token = ++renderToken;
  let content;
  try { content = payload(); }
  catch (error) { svgOutput = ''; const ctx = canvas.getContext('2d'); ctx.clearRect(0, 0, canvas.width, canvas.height); message('qr-status', error.message, 'warning'); return; }
  const size = Number(byId('qr-size').value);
  const style = getStyle();
  const fg = byId('qr-fg-color').value;
  const bg = byId('qr-bg-color').value;
  const correctionEl = byId('qr-correction');
  if (logoImage && correctionEl) {
    correctionEl.value = 'H';
    correctionEl.disabled = true;
  }
  const correction = logoImage ? 'H' : (correctionEl?.value || 'M');
  try {
    const matrix = QRCode.create(content, { errorCorrectionLevel: correction });
    if (token !== renderToken) return;
    drawMatrix(matrix.modules, size, style, fg, bg);
    svgOutput = makeSvg(matrix.modules, size, style, fg, bg);
    if (contrastRatio(fg, bg) < 4.5) message('qr-status', isAr ? 'التباين منخفض وقد يصعب مسح الرمز. اختر لونًا أغمق وخلفية أفتح.' : 'Low contrast may make this code difficult to scan. Choose a darker foreground and lighter background.', 'warning');
    else message('qr-status', logoImage ? (isAr ? 'جاهز · رُفع تصحيح الأخطاء إلى H ومقفل لحماية الشعار.' : 'Ready · error correction locked to H for logo protection.') : (isAr ? `جاهز · تصحيح ${correction} · ${style === 'dots' ? 'نقاط' : style === 'rounded' ? 'دائري' : 'مربع'}` : `Ready · ${correction} · ${style}`), 'success');
  } catch (error) { svgOutput = ''; message('qr-status', error.message, 'error'); }
}

byId('qr-content-type').addEventListener('change', setType);
byId('qr-wifi-security').addEventListener('change', () => {
  const password = byId('qr-wifi-password');
  password.disabled = byId('qr-wifi-security').value === 'nopass';
  if (password.disabled) password.value = '';
  render();
});
for (const id of formFields.slice(1)) byId(id)?.addEventListener('input', render);
byId('qr-content-type').addEventListener('input', render);
app.querySelectorAll('input[name="qr-style-choice"]').forEach((radio) => {
  radio.addEventListener('change', () => {
    const s = byId('qr-style');
    if (s) s.value = radio.value;
    render();
  });
});
app.querySelectorAll('.qr-preset-btn').forEach((button) => button.addEventListener('click', () => {
  const type = button.dataset.type;
  if (type) {
    byId('qr-content-type').value = type;
    setType();
    if (type === 'whatsapp') {
      const waPhone = byId('qr-whatsapp-number');
      if (waPhone && !waPhone.value) waPhone.value = '1000000000';
    }
  }
  if (button.dataset.value) byId('qr-input-text').value = button.dataset.value;
  if (button.dataset.ssid) byId('qr-ssid').value = button.dataset.ssid;
  render();
}));
byId('qr-logo-input').addEventListener('change', () => {
  const file = byId('qr-logo-input').files[0];
  if (!file) return;
  if (!file.type.startsWith('image/')) { message('qr-status', isAr ? 'اختر صورة PNG أو JPEG أو WebP.' : 'Choose a PNG, JPEG, or WebP image.', 'error'); return; }
  if (file.size >= 2 * 1024 * 1024) {
    showFileAdvisory(
      byId('qr-logo-input').closest('.qr-brand-row') || app,
      isAr
        ? `شعار بدقة عالية (${formatBytes(file.size)}) — سيتم تقليصه تلقائياً ليناسب أبعاد الرمز.`
        : `High-resolution logo (${formatBytes(file.size)}) — will be auto-scaled to fit QR code size.`
    );
  } else {
    showFileAdvisory(byId('qr-logo-input').closest('.qr-brand-row') || app, '', false);
  }
  const url = URL.createObjectURL(file); const image = new Image();
  image.onload = () => {
    logoImage = image;
    const temp = document.createElement('canvas');
    temp.width = 512;
    temp.height = 512;
    const ctx = temp.getContext('2d');
    const scale = 512 / Math.max(image.naturalWidth, image.naturalHeight);
    const w = image.naturalWidth * scale;
    const h = image.naturalHeight * scale;
    ctx.drawImage(image, (512 - w) / 2, (512 - h) / 2, w, h);
    logoDataUrl = temp.toDataURL('image/png');
    URL.revokeObjectURL(url);
    byId('qr-logo-name').textContent = file.name;
    const clearBtn = byId('qr-logo-clear');
    if (clearBtn) clearBtn.hidden = false;
    const correctionEl = byId('qr-correction');
    if (correctionEl) {
      correctionEl.value = 'H';
      correctionEl.disabled = true;
      correctionEl.title = isAr ? 'تم قفل تصحيح الأخطاء على H لوجود شعار.' : 'Error correction locked to H for logo protection.';
    }
    render();
  };
  image.onerror = () => { URL.revokeObjectURL(url); message('qr-status', isAr ? 'تعذر فتح الشعار.' : 'Could not open the logo.'); };
  image.src = url;
});
byId('qr-logo-clear').addEventListener('click', () => {
  logoImage = null;
  logoDataUrl = '';
  byId('qr-logo-input').value = '';
  byId('qr-logo-name').textContent = isAr ? 'بدون شعار' : 'No logo';
  const clearBtn = byId('qr-logo-clear');
  if (clearBtn) clearBtn.hidden = true;
  const correctionEl = byId('qr-correction');
  if (correctionEl) {
    correctionEl.disabled = false;
    correctionEl.title = '';
  }
  render();
});
byId('qr-download-png-btn').addEventListener('click', () => { if (!svgOutput) return; canvas.toBlob((blob) => { if (blob) { saveBlob(blob, 'kazakan-qrcode.png'); trackEvent('tool_completed', 'qr-code-generator', { format: 'png' }); } }, 'image/png'); });
byId('qr-download-svg-btn').addEventListener('click', () => { if (!svgOutput) return; saveBlob(new Blob([svgOutput], { type: 'image/svg+xml;charset=utf-8' }), 'kazakan-qrcode.svg'); trackEvent('tool_completed', 'qr-code-generator', { format: 'svg' }); });

// Initialize Modern Phone & Country Selector for WhatsApp Preset
attachModernPhoneInput({
  fieldEl: byId('qr-wa-phone-field'),
  countryBtnEl: byId('qr-wa-country-btn'),
  flagEl: byId('qr-wa-current-flag'),
  dialBadgeEl: byId('qr-wa-dial-code-badge'),
  dropdownEl: byId('qr-wa-country-dropdown'),
  searchInputEl: byId('qr-wa-country-search'),
  listEl: byId('qr-wa-country-list'),
  phoneInputEl: byId('qr-whatsapp-number'),
  dialInputEl: byId('qr-wa-dial-code'),
  isAr,
  onUpdate: render
});

trackEvent('tool_started', 'qr-code-generator');
render();

